/**
 * PLP discount badge — "14% OFF" next to the sale price on product cards, same as the PDP
 * (the PDP label comes from the `products.product.price.percent_off` locale string).
 *
 * The variant-swatch-king app rewrites `.card__price` on first load and on every variant
 * change with its own template (configured in the app admin, not in the theme):
 *   <div class="price price--on-sale">
 *     <span class="price-item price-item--regular">$699.99</span>   (compare-at, struck)
 *     <span>&nbsp;</span>
 *     <span class="price-item price-item--sale">$599.99</span>
 *   </div>
 * That template has no badge, so we watch the card price and add it ourselves.
 */

const CARD_PRICE_SELECTOR = '.card__price';
const REGULAR_SELECTOR = '.price-item--regular';
const SALE_SELECTOR = '.price-item--sale';
// Badge already rendered by the theme's own price snippet (price.liquid) — nothing to add.
const THEME_BADGE_SELECTOR = '.price-item--percent';
const BADGE_CLASS = 'card-discount-badge';

// Only the digits matter: both amounts share the same format, so the ratio is the same
// whether the store shows decimals or not ("$699.99" -> 69999).
const toAmount = (el) =>
  parseInt((el?.textContent || '').replace(/\D/g, ''), 10) || 0;

function updateCardBadge(cardPrice) {
  if (cardPrice.querySelector(THEME_BADGE_SELECTOR)) return;

  const regular = cardPrice.querySelector(REGULAR_SELECTOR);
  const sale = cardPrice.querySelector(SALE_SELECTOR);
  const badge = cardPrice.querySelector(`.${BADGE_CLASS}`);

  const compareAt = toAmount(regular);
  const price = toAmount(sale);
  // Non-sale variants come with the compare-at hidden ("$0"), so this also clears the badge.
  // Floor, like price.liquid's integer `divided_by`, so PLP and PDP always show the same number.
  const percent =
    compareAt > price && price > 0
      ? Math.floor(((compareAt - price) * 100) / compareAt)
      : 0;

  if (!percent) {
    badge?.remove();
    return;
  }

  const text = `${percent}% OFF`;
  if (
    badge &&
    badge.textContent === text &&
    badge.previousElementSibling === sale
  )
    return;

  const next = badge || document.createElement('span');
  next.className = BADGE_CLASS;
  next.textContent = text;
  sale.after(next);
}

function collectCardPrices(mutations) {
  const cardPrices = new Set();

  for (const mutation of mutations) {
    const target =
      mutation.target.nodeType === Node.ELEMENT_NODE
        ? mutation.target
        : mutation.target.parentElement;
    const owner = target?.closest(CARD_PRICE_SELECTOR);
    if (owner) cardPrices.add(owner);

    // Whole cards added at once (facet filters / pagination re-render the grid)
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches(CARD_PRICE_SELECTOR)) cardPrices.add(node);
      node
        .querySelectorAll(CARD_PRICE_SELECTOR)
        .forEach((el) => cardPrices.add(el));
    });
  }

  return cardPrices;
}

function initPlpDiscountBadge() {
  // Theme setting "Show discount price" off (layout/theme.liquid) — no badge on cards
  if (document.body.hasAttribute('data-hide-discount-price')) return;

  document.querySelectorAll(CARD_PRICE_SELECTOR).forEach(updateCardBadge);

  // Adding the badge triggers this observer again, but updateCardBadge is a no-op when
  // the badge is already up to date, so it settles after one extra pass.
  new MutationObserver((mutations) => {
    collectCardPrices(mutations).forEach(updateCardBadge);
  }).observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPlpDiscountBadge);
} else {
  initPlpDiscountBadge();
}
